import axios from "axios";
import {
  showGlobalToast,
  markSubscriptionExpired,
} from "./utils/globalNotifications";

export const API_BASE_URL =
    process.env.REACT_APP_API_BASE || "http://localhost:8000/api";

export const API_BASE =
  process.env.REACT_APP_API_BASE + '/v1' || "http://localhost:8000/api/v1";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE || "http://localhost:8000/api",
  withCredentials: true,
});

export default api;

const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN ||
  (typeof API_BASE === "string"
    ? API_BASE.replace(/\/api(\/v1)?(\/.*)?$/, "")
    : "http://localhost:8000");

export async function ensureCsrfCookie() {
  try {
    const url = `${API_ORIGIN}/sanctum/csrf-cookie`;
    await axios.get(url, { withCredentials: true });
    return true;
  } catch (e) {
    return false;
  }
}

const STORAGE_PREFIX = "wv_";
const TOKEN_KEY = STORAGE_PREFIX + "token";
const API_KEY_KEY = STORAGE_PREFIX + "api_key";

function readStoredValue(key) {
  try {
    const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === "object" &&
        Object.prototype.hasOwnProperty.call(parsed, "value")
      ) {
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) return null;
        return parsed.value;
      }
      return parsed;
    } catch (e) {
      return raw;
    }
  } catch (e) {
    return null;
  }
}

api.interceptors.request.use(
  (config) => {
    try {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        const token = readStoredValue(TOKEN_KEY);

        // if token is null logout
        // if path is not login
        let currentPath = window.location.pathname;
        if(!token && currentPath !== "/") {
            window.location.href = '/';
            return false;
        }

        if (token) config.headers.Authorization = `Bearer ${token}`;
      }
      if (!config.headers["X-API-KEY"]) {
        const apiKey = readStoredValue(API_KEY_KEY);
        if (apiKey) config.headers["X-API-KEY"] = apiKey;
      }
      try {
        const selectedCompany = readStoredValue(
          STORAGE_PREFIX + "selected_company_id"
        );
        if (selectedCompany) {
          config.headers = config.headers || {};
          config.headers["X-Company-Id"] = selectedCompany;
        }
      } catch (e) {}
    } catch (e) {}
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (res) => {
    try {
      const body = res?.data;
      const msg = body?.message || (typeof body === "string" ? body : null);
      const status = body?.status;
      const reqUrl = res?.config?.url || "";
      const reqMethod = (res?.config?.method || "").toLowerCase();
      if (reqMethod === "post" && /\/login(?:$|\?|\/)/i.test(reqUrl)) {
        return res;
      }

      if (status === "Error" && msg && /expir|subscription/i.test(msg)) {
        try {
          const selectedCompany = readStoredValue(
            STORAGE_PREFIX + "selected_company_id"
          );
          const companyIdFromBody =
            body?.data?.company_id ||
            body?.data?.company?.id ||
            body?.company_id ||
            body?.company?.id;

          if (
            selectedCompany &&
            companyIdFromBody &&
            String(selectedCompany) === String(companyIdFromBody)
          ) {
            const shouldShowToast = markSubscriptionExpired();
            if (shouldShowToast) showGlobalToast(msg, "error", 6000);
          }
        } catch (e) {
          window.alert(msg);
        }
        return { data: {} };
      }

      if (status === "Success") {
        const apiKeys = body?.data?.api_keys;
        const companies = body?.data?.companies;
        const listToCheck = Array.isArray(companies)
          ? companies.map((c) => ({ company: c }))
          : Array.isArray(apiKeys)
          ? apiKeys
          : [];

        if (Array.isArray(listToCheck) && listToCheck.length > 0) {
          const nonActive = new Set([
            "expired",
            "inactive",
            "cancelled",
            "canceled",
            "past_due",
          ]);
          const selectedCompany = readStoredValue(
            STORAGE_PREFIX + "selected_company_id"
          );
          if (selectedCompany) {
            for (const keyData of listToCheck) {
              const companyId =
                keyData?.company?.id || keyData?.company_id || keyData?.id;
              if (companyId && String(selectedCompany) !== String(companyId))
                continue;
              const s =
                keyData?.company?.subscription_status ||
                keyData?.subscription_status;
              if (s && nonActive.has(String(s).toLowerCase())) {
                try {
                  const shouldShowToast = markSubscriptionExpired();
                  if (shouldShowToast) {
                    const expiredMsg = `Your subscription has expired or is inactive. Please renew your subscription to continue using the service.`;
                    showGlobalToast(expiredMsg, "error", 6000);
                  }
                } catch (e) {
                  const expiredMsg = `Your subscription has expired or is inactive. Please renew your subscription to continue using the service.`;
                  window.alert(expiredMsg);
                }
                break;
              }
            }
          }
        }
      }
    } catch (e) {}
    return res;
  },
  (err) => {
    try {
      const reqUrl = err?.config?.url || err?.response?.config?.url || "";
      const reqMethod = (err?.config?.method || "").toLowerCase();
      if (reqMethod === "post" && /\/(?:v1\/)?login(?:$|\?|\/)/i.test(reqUrl)) {
        return Promise.reject(err);
      }
      const msg = err?.response?.data?.message || err?.message;
      if (msg && /expir|subscription/i.test(msg)) {
        try {
          const selectedCompany = readStoredValue(
            STORAGE_PREFIX + "selected_company_id"
          );
          const companyIdFromBody =
            err?.response?.data?.data?.company_id ||
            err?.response?.data?.data?.company?.id ||
            err?.response?.data?.company_id ||
            err?.response?.data?.company?.id;

          if (
            selectedCompany &&
            companyIdFromBody &&
            String(selectedCompany) === String(companyIdFromBody)
          ) {
            const shouldShowToast = markSubscriptionExpired();
            if (shouldShowToast) showGlobalToast(msg, "error", 6000);
          }
        } catch (e) {
          window.alert(msg);
        }
        return Promise.resolve({ data: {} });
      }
    } catch (e) {}
    return Promise.reject(err);
  }
);

export async function fetchList(
  endpoint,
  { headers = {}, params = {}, signal, swallow = true } = {}
) {
  const url =
    endpoint && endpoint.startsWith("http")
      ? endpoint
      : `${API_BASE_URL}${
          endpoint && endpoint.startsWith("/") ? "" : "/"
        }${endpoint}`;

  console.log("Fetching", {API_BASE_URL, url});

  try {
    const res = await api.get(url, { headers, params, signal });

    const body = res?.data;

    const asArray = (cand) => (Array.isArray(cand) ? cand : undefined);

    let rows =
      asArray(body) ||
      asArray(body?.data) ||
      asArray(body?.data?.data) ||
      asArray(body?.data?.rows) ||
      asArray(body?.rows) ||
      asArray(body?.items) ||
      asArray(body?.results) ||
      null;

    if (!rows && body && typeof body === "object") {
      for (const k of Object.keys(body)) {
        if (Array.isArray(body[k])) {
          rows = body[k];
          break;
        }
      }
    }

    return Array.isArray(rows) ? rows : [];
  } catch (err) {
    if (axios.isCancel(err)) return [];
    if (swallow) return [];
    throw err;
  }
}

export function inferFirstKey(keys, patterns) {
  for (const p of patterns) {
    const regex = p instanceof RegExp ? p : new RegExp(p, "i");
    const found = keys.find((k) => regex.test(k));
    if (found) return found;
  }
  return keys[0];
}
